import type { RegistrationListItem } from '@/hooks/use-registrations';
import { getEffectiveRegistrationField } from './utils';

export type ConsolidatedPaymentStatus = 'paid' | 'partial' | 'unpaid';
export type BalanceInvoiceOverviewStatus = 'created' | 'partial' | 'failed' | 'not_created';

export function usesEffectiveSplitBilling(registration: RegistrationListItem): boolean {
  if (!registration.isSplitBilling) return false;

  const participantsRaw = getEffectiveRegistrationField(
    registration.currentData,
    registration.manualOverrides,
    'number_of_participants',
    '1'
  ).value;
  const participantCount = Number.parseInt(participantsRaw, 10);

  return Number.isFinite(participantCount) && participantCount > 1;
}

function getLatestPaymentPerPerson(registration: RegistrationListItem) {
  const latestByPerson = new Map<number, RegistrationListItem['payments'][number]>();

  for (const payment of registration.payments || []) {
    const existing = latestByPerson.get(payment.personNumber);
    if (!existing || new Date(payment.createdAt).getTime() > new Date(existing.createdAt).getTime()) {
      latestByPerson.set(payment.personNumber, payment);
    }
  }

  return latestByPerson;
}

export function getConsolidatedPaymentStatus(registration: RegistrationListItem): ConsolidatedPaymentStatus {
  const expectedPersons = usesEffectiveSplitBilling(registration) ? [1, 2] : [1];
  const latestByPerson = getLatestPaymentPerPerson(registration);
  const paidCount = expectedPersons.filter(person => latestByPerson.get(person)?.status === 'paid').length;

  if (paidCount === expectedPersons.length) return 'paid';
  if (paidCount > 0) return 'partial';
  return 'unpaid';
}

export function getBalanceInvoiceOverviewStatus(registration: RegistrationListItem): BalanceInvoiceOverviewStatus {
  const expectedCount = usesEffectiveSplitBilling(registration) ? 2 : 1;
  const balanceInvoices = registration.invoices.filter(invoice => invoice.type === 'BALANCE');
  const completedCount = balanceInvoices.filter(
    invoice =>
      invoice.exactStatus === 'SYNCED' ||
      invoice.exactStatus === 'MATCHED' ||
      invoice.exactStatus === 'MANUALLY_RESOLVED'
  ).length;

  if (balanceInvoices.some(invoice => invoice.exactStatus === 'FAILED')) return 'failed';
  if (completedCount === expectedCount) return 'created';
  if (completedCount > 0) return 'partial';
  return 'not_created';
}
